import React, { ChangeEvent, Key, useEffect, useState } from 'react';

import { ApolloError } from '@apollo/client';
import type { MenuProps } from 'antd';
import {
  Form,
  Table,
  Tooltip,
  Upload,
  message,
  Menu,
  Space,
  Collapse,
} from 'antd';
import { ColumnsType, ColumnType, TableProps } from 'antd/es/table';
import { AnyObject } from 'antd/es/_util/type';
import { ItemType } from 'antd/es/breadcrumb/Breadcrumb';
import {
  ReloadOutlined,
  SettingOutlined,
  DownloadOutlined,
  FileExcelTwoTone,
  UploadOutlined,
} from '@ant-design/icons';
import { SubmitHandler, UseFormReturn } from 'react-hook-form';

import { read, utils } from 'xlsx';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { isArray } from 'lodash';

import { filterRecordById } from '../../../utils/filterRecordById';
import { convertValuesToNumbers } from '../../../utils/convertValuesToNumbers';
import { cleanUpObject } from '../../../utils';
import { formattedMoney } from '../../../pages/bcp/strategies/strategyReview/utils';
import { ErrorMessages, LOCATION_TYPES } from '../../../constants';

import { ExportColumn } from '../../../interfaces/ExportColumn';
import { useUserContext } from '../../../context/PermissionsContext';
import { useFilteredData } from '../../../hooks';

import BorderedSection from '../BorderedSection';
import FilterHeader from '../FilterHeader';
import AntModal from '../AntModal';
import AutomatedForm from '../Form/AutomatedForm';
import DeleteModal from '../DeleteModal';
import { EditableCell, EditableCellMitigation } from '../EditableCell';
import { Button } from '../../Button';
import { CheckIcon, ModalClose, QuickEdit, RecycleBin } from '../../assets';

import { ApplicationDependency } from '../../../pages/cyber';

import './TableV3.css';
import ChevronDownIcon from '../../assets/ChevronDownIcon';
import ChevronUpIcon from '../../assets/ChevronUpIcon';

const { Panel } = Collapse;

type MenuItem = Required<MenuProps>['items'][number];

type actionType = 'create' | 'update';

export type ModifyVariablesArgs = {
  values?: any;
  type?: actionType;
  currentRecord?: any;
  locationId?: string;
};

interface GraphQLVariables {
  [key: string]: any;
  shouldOmitUuid?: boolean;
}

type MatrixSubmitType =
  | 'location'
  | 'processAreas'
  | 'processes'
  | 'suppliers'
  | 'applications';

type TableV3Props = {
  title?: string | ((record: any) => string);
  data: any;
  loading?: boolean;
  error?: ApolloError;
  columns: ColumnsType<any>;
  editable?: boolean;
  formFields?: any;
  editFormFields?: any;
  createGQL?: any;
  updateGQL?: any;
  deleteGQL?: any;
  summary?: any;
  modifyVariables?: (args: ModifyVariablesArgs) => GraphQLVariables;
  extendActions?: (record?: any) => JSX.Element | null;
  editableOptions?: { create?: boolean; update?: boolean; delete?: boolean };
  forceSubmit?: boolean;
  forceSubmitRecordIndex?: string | null | undefined;
  customStyles?: string;
  useBorderedSection?: boolean;
  inlineEditing?: boolean;
  isMatrix?: boolean;
  matrixConfig?: any;
  isMitigation?: boolean;
  mitigatedLocations?: any;
  matrixType?: MatrixSubmitType;
  scroll?: TableProps<AnyObject>['scroll'];
  percentageValidation?: boolean;
  childrenColumnName?: string;
  rowKey?: string;
  exportExcelColumns?: ExportColumn[];
  templateColumns?: any[];
  templateUploadConfig?: any;
  onRefresh?: () => void;
  rowSelection?: any;
  selectedRows?: any;
  multiSelectComponent?: any;
  formInstance?: UseFormReturn<any>;
  extendOptions?: JSX.Element | null;
  tableSize?: 'small' | 'middle' | 'large';
  excludeRows?: {
    identifier: string;
    value: string;
  }[];
  hideTableHeading?: boolean;
  nonSearchableColumns?: string[];
  searchPlaceholder?: string;
  searchOnlyPrincipalData?: boolean;
  pageSize?: number;
  /** Custom delete handler to override the default delete functionality.
   * When provided, this function will be called instead of the standard delete mutation. */
  customDeleteHandler?: (record: any) => void;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  onCollapseChange?: (collapsed: boolean) => void;
};

const MemoizedRow: React.FC<any> = React.memo((props) => {
  const { className, style, ...restProps } = props;
  return <tr className={className} style={style} {...restProps} />;
});

const PROPERTIES = {
  location: 'locationAuxs',
  processAreas: 'processAreaDependencys',
  processes: 'procesessDependencys',
  suppliers: 'suppliers',
  applications: 'applications',
};

const TableV3 = ({
  title,
  data,
  loading,
  columns,
  editable = false,
  formFields,
  editFormFields,
  createGQL,
  updateGQL,
  deleteGQL,
  summary,
  modifyVariables,
  extendActions,
  editableOptions = { create: true, update: true, delete: true },
  forceSubmit,
  forceSubmitRecordIndex,
  customStyles,
  useBorderedSection = true,
  inlineEditing = false,
  isMatrix = false,
  matrixConfig,
  matrixType = 'location',
  scroll,
  percentageValidation = false,
  childrenColumnName,
  rowKey,
  isMitigation = false,
  mitigatedLocations,
  exportExcelColumns,
  templateColumns,
  templateUploadConfig,
  onRefresh,
  rowSelection,
  selectedRows,
  multiSelectComponent: MultiSelectComponent,
  formInstance,
  extendOptions,
  tableSize,
  excludeRows,
  hideTableHeading = true,
  nonSearchableColumns,
  searchPlaceholder,
  searchOnlyPrincipalData,
  pageSize,
  customDeleteHandler,
  collapsible = false,
  defaultCollapsed = false,
  onCollapseChange,
}: TableV3Props) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [currentRecordIndex, setCurrentRecordIndex] = useState<
    string | number | null
  >(null);
  const [currentRecord, setCurrentRecord] = useState<any>(null);
  const { canViewFinancials, canExport } = useUserContext();

  const [currentInlineRecord, setCurrentInlineRecord] = useState<any>(null);
  const [isDeleteInlineModalOpen, setIsDeleteInlineModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [form] = Form.useForm();

  const { isAdmin, canEdit } = useUserContext();

  const filteredData = useFilteredData({
    data,
    searchTerm,
    isMatrix,
    nonSearchableColumns,
    searchOnlyPrincipalData,
    childrenColumnName,
  });

  const isEditingRow = (recordId: string) => recordId === currentRecordIndex;

  const canMakeEditableActions =
    editable && editableOptions.update && (isAdmin || canEdit);

  const canMakeCreateActions =
    editable && editableOptions.create && (isAdmin || canEdit);

  const isEditingInlineRow = (recordId: string) =>
    recordId === currentInlineRecord?.key ||
    recordId === currentInlineRecord?.uuid;

  const renderTitle =
    typeof title === 'function' ? title(currentRecord) : title;

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value.toLowerCase());
  };

  const handleCollapseChange = (key: string | string[]) => {
    const collapsed = Array.isArray(key) ? key.length === 0 : !key;
    onCollapseChange?.(collapsed);
  };

  const getDefaultValue = (type: string) => {
    switch (type) {
      case 'text':
        return '';
      case 'percentage':
        return '0%';
      case 'money':
        return '$0.00';
      case 'number':
        return 0;
      default:
        return '';
    }
  };

  const formatValue = (value: any, type: string) => {
    switch (type) {
      case 'percentage':
        return value !== undefined ? Number(value) / 100 : 0;
      case 'money':
        return formattedMoney(Number(value));
      case 'text':
        return value;
      default:
        return value;
    }
  };

  type DataType = Record<string, any>;

  const exportToExcel = async (
    data: DataType[],
    columns: ExportColumn[],
    fileName = 'table-data.xlsx',
    childrenKey = 'children',
    indentSize = 2,
  ) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet1');

    worksheet.columns = columns.map(({ key, header, width = 25 }) => ({
      key,
      header,
      width,
    }));

    const addRowWithChildren = (row: DataType, indentLevel: number = 0) => {
      const newRow: Record<string, any> = {};

      columns.forEach(({ key, type, placeholder, nestedKey }, columnIndex) => {
        let value;
        if (nestedKey) {
          value = getNestedValue(row, nestedKey, columnIndex);
        } else {
          value = getNestedValue(row, key, columnIndex);
        }

        let displayValue = formatValue(value, type);

        if (
          displayValue === null ||
          displayValue === undefined ||
          displayValue === ''
        ) {
          displayValue = placeholder ?? getDefaultValue(type);
        }

        // Apply indentation and prefix only for the first column
        if (columnIndex === 0) {
          if (indentLevel === 1) {
            // For second level, add indentation only
            displayValue = `${' '.repeat(
              indentSize * indentLevel,
            )}${displayValue}`;
          } else if (indentLevel === 2) {
            // For third level, add indentation and a dot
            displayValue = `${' '.repeat(
              indentSize * indentLevel,
            )}• ${displayValue}`;
          } else if (indentLevel === 3) {
            // For fourth level, add indentation and a dash
            displayValue = `${' '.repeat(
              indentSize * indentLevel,
            )}- ${displayValue}`;
          }
        }

        newRow[key] = displayValue;
      });

      worksheet.addRow(newRow);

      // If the row has nested children, add them recursively
      const children = row[childrenKey];
      if (Array.isArray(children)) {
        children.forEach((childRow) => {
          addRowWithChildren(childRow, indentLevel + 1);
        });
      }
    };

    data.forEach((row) => {
      addRowWithChildren(row);
    });

    columns.forEach(({ key, type }) => {
      if (type === 'number') {
        worksheet
          .getColumn(key)
          .eachCell({ includeEmpty: true }, (cell, rowNumber) => {
            if (rowNumber > 1) {
              cell.value = Number(cell.value);
            }
          });
      }

      if (type === 'percentage') {
        worksheet
          .getColumn(key)
          .eachCell({ includeEmpty: true }, (cell, rowNumber) => {
            if (rowNumber > 1) {
              cell.value = Number(cell.value);
              cell.numFmt = '0.00%';
            }
          });
      }

      if (type === 'money') {
        worksheet
          .getColumn(key)
          .eachCell({ includeEmpty: true }, (cell, rowNumber) => {
            if (
              rowNumber > 1 &&
              typeof cell.value === 'number' &&
              !isNaN(cell.value)
            ) {
              cell.numFmt = '$0.00';
            } else {
              cell.alignment = { horizontal: 'right' };
            }
          });
      }

      if (type === 'date') {
        worksheet
          .getColumn(key)
          .eachCell({ includeEmpty: true }, (cell, rowNumber) => {
            if (rowNumber > 1 && typeof cell.value === 'string') {
              const parsedDate = new Date(cell.value); // Parse the ISO string to a Date object
              cell.value = parsedDate; // Set the cell value as a Date object
              cell.numFmt = 'dd/mm/yyyy'; // Apply the date format
            }
          });
      }
    });

    worksheet.eachRow({ includeEmpty: true }, (row) => {
      row.eachCell({ includeEmpty: true }, (cell) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
      });
    });

    worksheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = { horizontal: 'center' };
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, fileName);
  };

  const getNestedValue = (obj: any, key: string, columnIndex?: number) => {
    const [firstKey, index, secondKey] = key.split('.');

    if (Array.isArray(obj[firstKey])) {
      if (columnIndex !== undefined) {
        return obj[firstKey]?.[Number(index)]?.[secondKey];
      }
    }

    return key.split('.').reduce((acc, part) => {
      if (Array.isArray(acc)) {
        return acc?.map((item: any) => item[part]).join(', ');
      }

      return acc?.[part];
    }, obj);
  };

  const baseActions = (record: any) => {
    const shouldIgnoreRow = excludeRows?.some((excludedRow: any) => {
      return (
        excludedRow?.identifier in record &&
        record[excludedRow?.identifier] === excludedRow.value
      );
    });

    if (shouldIgnoreRow) return;

    return extendActions?.(record) || canMakeEditableActions ? (
      <div className="w-full flex items-center justify-end pr-4 gap-2.5">
        {canMakeEditableActions && (
          <Tooltip title="Edit">
            <div>
              <Button
                type="edit"
                className="!w-[25px] !h-[25px] flex-shrink-0"
                onClick={() =>
                  isMitigation
                    ? handleInlineEditMitigation(record)
                    : (() => {
                        if (inlineEditing) {
                          handleInlineEdit(record);
                        } else {
                          handleEditRecord(record);
                        }
                      })()
                }
              >
                <QuickEdit />
              </Button>
            </div>
          </Tooltip>
        )}
        {extendActions?.(record)}
      </div>
    ) : null;
  };

  const handleInlineSubmit = () => {
    if (!form.isFieldsTouched()) {
      setCurrentInlineRecord(null);
      setCurrentRecordIndex(null);
      setCurrentRecord(null);

      return;
    }

    const errors = form.getFieldsError();
    const hasErrors = errors.some((error) => error.errors.length > 0);

    if (hasErrors) {
      messageApi.open({
        type: 'error',
        content: 'Please check the form for errors',
      });
      return;
    }

    form
      .validateFields()
      .then((values) => {
        isMatrix
          ? onMatrixSubmit({ values: cleanUpObject(values), type: matrixType })
          : onSubmit(values);
        setCurrentInlineRecord(null);
        setCurrentRecordIndex(null);
        setCurrentRecord(null);
      })
      .catch((errorInfo) => {
        messageApi.open({
          type: 'error',
          content: errorInfo || 'Please check the form for errors',
        });
      });
  };

  const inlineEditingActions = (record: DataType) => {
    return (
      <div className="flex items-center justify-end pr-4 gap-2.5">
        {editableOptions.update && (
          <>
            <div>
              <Button
                type={'edit'}
                className="!w-[25px] !h-[25px] flex-shrink-0"
                onClick={isMitigation ? save : handleInlineSubmit}
              >
                <CheckIcon />
              </Button>
            </div>
            <div>
              <Button
                type={'edit'}
                className="!w-[25px] !h-[25px] flex-shrink-0"
                onClick={() => {
                  setCurrentInlineRecord(null);
                  setCurrentRecordIndex(null);
                  setCurrentRecord(null);
                }}
              >
                <ModalClose />
              </Button>
            </div>
          </>
        )}
        {editableOptions.delete && (
          <div>
            <Button
              type="edit"
              className="!w-[25px] !h-[25px] flex-shrink-0"
              onClick={() => setIsDeleteInlineModalOpen(true)}
            >
              <RecycleBin />
            </Button>
          </div>
        )}
      </div>
    );
  };

  const actionColumns = [
    {
      title: '',
      key: 'edit',
      dataIndex: 'edit',
      fixed: 'right' as const,
      width: 100,
      render: (_: string, record: DataType) => {
        const isInlineEditing =
          inlineEditing &&
          (isEditingRow(String(record?.uuid ?? record?.key)) ||
            isEditingInlineRow(String(record?.uuid ?? record?.key)));

        return isInlineEditing
          ? inlineEditingActions(record)
          : baseActions(record);
      },
    },
  ];

  const customColumns = [...columns];

  if (canMakeEditableActions || extendActions) {
    customColumns.push(...actionColumns);
  }

  const handleAddRecord = () => {
    setIsModalOpen(true);
  };

  const handleEditRecord = (record: any) => {
    setIsModalOpen(true);
    setCurrentRecordIndex(record.uuid || record.key);
    setCurrentRecord(record);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentRecordIndex(null);
    setCurrentRecord(null);
  };

  const handleInlineEdit = (record: DataType) => {
    if (isMatrix) {
      const flattenLocations: { [key: string]: any } = {};
      const propertyToMap =
        matrixConfig?.propertyToMap ?? PROPERTIES[matrixType];

      isArray(record[propertyToMap]) &&
        (record?.[propertyToMap] as any).forEach(
          (location: any, index: number) => {
            flattenLocations[`percentage_${index}`] =
              location.percentage ?? location.value.percentage;
          },
        );

      form.setFieldsValue({ ...record, ...flattenLocations });
      setCurrentInlineRecord(record);
      return;
    }

    form.setFieldsValue({ ...record });
    setCurrentRecord(record);
    setCurrentRecordIndex(String('key' in record ? record.key : record.uuid));
  };

  const handleInlineEditMitigation = (record: DataType) => {
    setCurrentRecordIndex(String(record.key));
    setCurrentInlineRecord(record);

    const propertyToMap = 'vendors' in record ? 'vendors' : 'locations';

    const filteredItems = record?.[propertyToMap].filter((item: any) => {
      return mitigatedLocations?.includes(item.uuid ?? item.key);
    });

    const flattenLocations: { [key: string]: any } = {};

    isArray(filteredItems) &&
      (filteredItems as any).forEach((location: any, index: number) => {
        flattenLocations[`percentage_${index}`] = location.percentage;
        flattenLocations[`delay_${index}`] = location.delay;
      });

    form.setFieldsValue({ ...record, ...flattenLocations });
  };

  const getMetadataFromFormFields = (key?: Key | undefined) => {
    if (!key) return { required: false, type: 'text', key: null };
    const field = formFields?.find((field: any) => field.key === key);

    return {
      key: field?.key,
      type: field?.type || 'text',
      required: field?.isRequired || false,
      defaultValue:
        field?.defaultValue ??
        (!field?.type || field?.type === 'text' ? '' : 0),
    };
  };

  const inlineEditingColumns =
    inlineEditing &&
    customColumns?.map((col) => {
      if (col.key === 'edit') return col;

      const { type, required, key, defaultValue } = getMetadataFromFormFields(
        col.key,
      );

      return {
        ...col,
        onCell: (record: DataType) => {
          const actualKey = 'key' in record ? record.key : record.uuid;

          return {
            record,
            title: col.title,
            dataIndex: 'dataIndex' in col ? col.dataIndex : col.key,
            inputType: type,
            required,
            editing:
              (key && isMatrix && isEditingInlineRow(String(actualKey))) ||
              (key && isEditingRow(String(actualKey))),
            defaultValue,
          };
        },
      };
    });

  const save = async () => {
    if (!form.isFieldsTouched()) {
      setCurrentRecordIndex(null);
      setCurrentInlineRecord(null);
      setCurrentRecord(null);
      return;
    }

    const errors = form.getFieldsError();
    const hasErrors = errors.some((error) => error.errors.length > 0);

    if (hasErrors) {
      messageApi.open({
        type: 'error',
        content: 'Please check the form for errors',
      });
      return;
    }

    try {
      const row = await form.validateFields();

      let changesByItem: {
        [key: string]: {
          percentage?: number;
          delay?: number;
          item: any;
        };
      } = {};

      Object.keys(row).forEach((key) => {
        const [field, index] = key.split('_');

        const currentColumn = columns?.find(
          (col) =>
            (col as ColumnType<any> & { colIndex?: number }).colIndex === parseInt(index),
        );

        let item;

        if ('locations' in currentInlineRecord) {
          item = currentInlineRecord?.locations?.find(
            (loc: any) => (loc.uuid ?? loc.key) === currentColumn?.key,
          );
        }

        if ('vendors' in currentInlineRecord) {
          item = currentInlineRecord?.vendors[index];
        }

        if (field === 'percentage' || field === 'delay') {
          const originalValue = parseFloat(item[field].toString());
          const newValue = parseFloat(row[key]);

          if (originalValue !== newValue) {
            if ('uuid' in item) {
              changesByItem[item.uuid] = { ...changesByItem[item.uuid], item };
            }

            if ('key' in item) {
              changesByItem[item.key] = { ...changesByItem[item.key], item };
            }

            changesByItem[item.uuid || item.key] = {
              ...changesByItem[item.uuid || item.key],
              [field]: newValue,
            };
          }
        }
      });

      await Promise.all(
        Object.values(changesByItem).map(
          async ({ percentage, delay, item }) => {
            if (item) {
              const { mutation, ...rest } =
                modifyVariables?.({ currentRecord: currentInlineRecord }) ?? {};

              await mutation({
                variables: {
                  data: {
                    data: [
                      {
                        delay: delay ?? item?.delay,
                        percentage: percentage ?? item?.percentage,
                        locationAux: {
                          uuid: item.key ?? item.uuid ?? '',
                        },
                        vendor: { uuid: item.key ?? item.uuid ?? '' },
                      },
                    ],
                    ...rest,
                  },
                },
              });
            }
          },
        ),
      );
      messageApi.open({
        type: 'success',
        content: 'Changes Saved',
      });
      form.resetFields();
    } catch (errInfo) {
      messageApi.open({
        type: 'error',
        content:
          errInfo instanceof Error ? errInfo.message : 'Operation Failed',
      });
      if (errInfo instanceof Error) console.log('error: ', errInfo?.message);
    } finally {
      setCurrentInlineRecord(null);
      setCurrentRecordIndex(null);
      setCurrentRecord(null);
    }
  };

  const inlineEditingColumnsMitigation =
    inlineEditing &&
    customColumns?.map((col: any) => {
      if (!col.editable) {
        return col;
      }

      const propertyToMap =
        matrixConfig?.propertyToMap ?? PROPERTIES?.[matrixType] ?? null;

      const property = !isMitigation && isMatrix ? propertyToMap : null;

      return {
        ...col,
        onCell: (record: DataType) => {
          return {
            record,
            editable: col.editable,
            dataIndex: col.dataIndex,
            colIndex: col.colIndex,
            title: col.title,
            propertyToMap:
              property ?? (record.vendors ? 'vendors' : 'locations'),
            editing:
              'key' in record
                ? isEditingInlineRow(String(record.key))
                : isEditingInlineRow(String(record.uuid)),
            onlyPercentage: !isMitigation,
          };
        },
      };
    });

  const onSubmit: SubmitHandler<any> = async (values: any) => {
    const mutation = currentRecord ? updateGQL : createGQL;
    const type = currentRecord ? 'update' : 'create';

    const data: Promise<GraphQLVariables> = modifyVariables
      ? modifyVariables({ values, type, currentRecord })
      : values;

    const { mutation: mutationToUse, shouldOmitUuid, ...rest } = await data;

    const chosenMutation = mutationToUse || mutation;

    setIsLoading && setIsLoading(true);

    try {
      if (!currentRecord) {
        await chosenMutation({
          variables: {
            data: {
              ...rest,
            },
          },
        });
      } else {
        await chosenMutation({
          variables: {
            data: {
              ...rest,
              ...(shouldOmitUuid ? {} : { uuid: currentRecord?.uuid }),
            },
          },
        });
      }
      messageApi.open({
        type: 'success',
        content: 'Changes Saved',
      });
    } catch (error) {
      messageApi.open({
        type: 'error',
        content:
          error instanceof Error && error.message
            ? error.message
            : ErrorMessages.OPERATION_FAILED,
      });
      if (error instanceof Error) console.log('error: ', error?.message);
    } finally {
      setIsLoading && setIsLoading(false);
      if (currentRecord) {
        setCurrentRecord(null);
        setCurrentRecordIndex(null);
      }
    }
  };

  const onMatrixSubmit = async ({
    values,
    type = 'location',
  }: {
    values: any;
    type?: MatrixSubmitType;
  }) => {
    let mutationToUse = updateGQL;

    const valuesCopy = convertValuesToNumbers(values);

    const propertyToMap = matrixConfig?.propertyToMap ?? PROPERTIES[type];

    if (percentageValidation && matrixType === 'location') {
      const locationPercentages = currentInlineRecord?.[propertyToMap]?.map(
        (item: any, index: number) => {
          if (item.locationType === LOCATION_TYPES.DISTRIBUTION_CENTER)
            return 0;
          return !Number.isNaN(valuesCopy[`percentage_${index}`])
            ? valuesCopy[`percentage_${index}`]
            : parseFloat(item.percentage.toString());
        },
      );

      const distributionCenterPercentages = currentInlineRecord?.[
        propertyToMap
      ]?.map((item: any, index: number) => {
        if (item.locationType === LOCATION_TYPES.DISTRIBUTION_CENTER) {
          return !Number.isNaN(valuesCopy[`percentage_${index}`])
            ? valuesCopy[`percentage_${index}`]
            : parseFloat(item.percentage.toString());
        }
        return 0;
      });

      const locationSum = locationPercentages?.reduce(
        (a: number, b: number) => a + b,
        0,
      );

      const distributionCenterSum = distributionCenterPercentages?.reduce(
        (a: number, b: number) => a + b,
        0,
      );

      if (locationSum > 100) {
        messageApi.open({
          type: 'error',
          content: 'Location percentages must sum up to 100',
        });
        form.resetFields();
        return;
      }

      if (distributionCenterSum > 100) {
        messageApi.open({
          type: 'error',
          content: 'Distribution Center percentages must sum up to 100',
        });
        form.resetFields();
        return;
      }
    }

    try {
      if (matrixType === 'location') {
        await Promise.all(
          (currentInlineRecord?.[propertyToMap] as any)?.map(
            async (item: any, index: number) => {
              const percentage = valuesCopy[`percentage_${index}`];

              const modifiedValue =
                !Number.isNaN(percentage) &&
                parseFloat(item.percentage.toString()) !== percentage;

              const data = modifyVariables
                ? modifyVariables({
                    currentRecord: currentInlineRecord,
                    locationId: item.key || item.uuid,
                  })
                : valuesCopy;

              const { mutation, ...rest } = data;

              mutationToUse = mutation;

              if (modifiedValue) {
                await mutationToUse({
                  variables: {
                    data: { ...rest, percentage },
                  },
                });
              }
            },
          ),
        );
      }

      if (matrixType === 'processAreas') {
        const processAreas = (
          currentInlineRecord?.[propertyToMap] as any
        )?.reduce((acc: any[], processArea: any, index: number) => {
          const percentage = valuesCopy[`percentage_${index}`];
          const modifiedValue =
            !Number.isNaN(percentage) &&
            parseFloat(processArea.percentage.toString()) !== percentage;

          if (modifiedValue) {
            acc.push({
              processAreaUUID: processArea.processAreaUUID,
              uuid: processArea.uuid,
              percentage,
            });
          }

          return acc;
        }, []);

        const data = modifyVariables
          ? modifyVariables({
              currentRecord: currentInlineRecord,
            })
          : valuesCopy;

        await mutationToUse({
          variables: {
            data: { ...data, processAreas },
          },
        });
      }

      if (matrixType === 'processes') {
        const procesess = (currentInlineRecord?.[propertyToMap] as any)?.reduce(
          (acc: any[], process: any, index: number) => {
            const percentage = valuesCopy[`percentage_${index}`];
            const modifiedValue =
              !Number.isNaN(percentage) &&
              parseFloat(process.percentage.toString()) !== percentage;

            if (modifiedValue) {
              acc.push({
                processUUID: process.processUUID,
                uuid: process.uuid,
                percentage,
              });
            }

            return acc;
          },
          [],
        );

        const data = modifyVariables
          ? modifyVariables({
              currentRecord: currentInlineRecord,
            })
          : valuesCopy;

        await mutationToUse({
          variables: {
            data: { ...data, procesess },
          },
        });
      }

      if (matrixType === 'suppliers') {
        const suppliers = (currentInlineRecord?.[propertyToMap] as any)?.reduce(
          (acc: any[], supplier: any, index: number) => {
            const percentage = valuesCopy[`percentage_${index}`];
            const modifiedValue =
              !Number.isNaN(percentage) &&
              parseFloat(supplier.percentage.toString()) !== percentage;

            if (modifiedValue) {
              acc.push({
                uuid: supplier.supplierDependencyUUID,
                percentage,
              });
            }

            return acc;
          },
          [],
        );

        const data = modifyVariables
          ? modifyVariables({
              currentRecord: currentInlineRecord,
            })
          : valuesCopy;

        mutationToUse = data.mutation;

        suppliers.forEach(async (values: any) => {
          await mutationToUse({ variables: { data: values } });
        });
      }

      if (matrixType === 'applications') {
        const applications = currentInlineRecord?.[propertyToMap]?.reduce(
          (acc: any[], application: ApplicationDependency, index: number) => {
            const percentage = valuesCopy[`percentage_${index}`];
            const modifiedValue =
              !Number.isNaN(percentage) &&
              parseFloat(application.value.percentage.toString()) !==
                percentage;

            if (modifiedValue) {
              acc.push({
                itApplicationsUuid: application.uuid,
                percentage,
              });
            }

            return acc;
          },
          [],
        );

        const data = modifyVariables
          ? modifyVariables({
              currentRecord: currentInlineRecord,
            })
          : valuesCopy;

        const dataToSend = {
          ...data,
          itApplicationValues: [...applications],
        };

        await mutationToUse({
          variables: {
            data: { ...dataToSend },
          },
        });
      }

      message.open({
        type: 'success',
        content: 'Changes Saved',
      });

      form.resetFields();
    } catch (error) {
      message.open({
        type: 'error',
        content:
          error instanceof Error
            ? error.message
            : ErrorMessages.OPERATION_FAILED,
      });

      if (error instanceof Error) console.log('error: ', error?.message);
    } finally {
      setCurrentRecordIndex(null);
    }
  };

  const onDelete: any = async () => {
    if (customDeleteHandler) {
      customDeleteHandler(currentRecord);
      return;
    }

    setIsLoading && setIsLoading(true);
    try {
      await deleteGQL({
        variables: {
          uuid: currentRecord?.uuid,
        },
      });
      messageApi.open({
        type: 'success',
        content: 'Entry Deleted',
      });
    } catch (error) {
      messageApi.open({
        type: 'error',
        content: 'Operation Failed',
      });
    } finally {
      setIsLoading && setIsLoading(false);
      setCurrentRecordIndex(null);
      setCurrentRecord(null);
    }
  };

  const generateExcelTemplate = async (
    sheetName: string,
    columnConfig: any[],
  ) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(sheetName);

    const FITING_NUMBER_OPTIONS = 3;

    worksheet.columns = columnConfig.map((col) => ({
      header: col.header,
      key: col.key,
      width: col.width,
    }));

    worksheet.addRow(
      columnConfig.map((col) => {
        if (col.options) {
          return col.options.length <= FITING_NUMBER_OPTIONS
            ? `Choose from: ${col.options.join(', ')}`
            : 'Choose from available options';
        }
        return col.type;
      }),
    );

    worksheet.addRow(templateColumns?.map(({ example }) => example));

    worksheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = { horizontal: 'center' };
    });
    worksheet.getRow(2).eachCell((cell) => {
      cell.alignment = { horizontal: 'center' };
    });
    worksheet.getRow(3).eachCell((cell) => {
      cell.alignment = { horizontal: 'center' };
    });

    columnConfig.forEach((col) => {
      worksheet.getColumn(col.key).eachCell((cell, rowNumber) => {
        if (rowNumber === 1 || rowNumber === 2) {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: col.required ? 'E7F3E8' : 'FFFF99' }, // Green for required, yellow for optional
          };
          cell.border = { left: { style: 'thin' }, right: { style: 'thin' } };
        } else if (rowNumber === 3) {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'dotted' },
            bottom: { style: 'dotted' },
            right: { style: 'dotted' },
          };
        }
      });
    });

    // Add data validation for select fields
    columnConfig.forEach((col) => {
      if (col.options) {
        worksheet.getColumn(col.key).eachCell((cell, rowNumber) => {
          if (rowNumber > 2) {
            cell.dataValidation = {
              type: 'list',
              allowBlank: !col.required,
              formulae: [`"${col.options.join(',')}"`],
            };
          }
        });
      }
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    saveAs(blob, `${sheetName}.xlsx`);
  };

  const handleFileUpload = (file: any, columnConfig: any[]) => {
    if (!file || !file.name.endsWith('.xlsx')) {
      message.error('Please upload a valid Excel file');
      return;
    }

    if (!templateUploadConfig) {
      console.error('Upload configuration is missing.');
      return;
    }

    const reader = new FileReader();

    reader.onload = (e: any) => {
      const data = new Uint8Array(e.target.result);
      const workbook = read(data, { type: 'array', cellDates: true });

      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      const jsonData = utils.sheet_to_json(worksheet, { header: 1 });

      const dataToSend = jsonData.slice(2).map((row: any) => {
        if (row[0] === templateUploadConfig?.sampleItemIdentifier) return;

        const rowData: any = {};
        columnConfig.forEach((col, index) => {
          rowData[col.key] = col?.hashMap
            ? col.hashMap[row[index]]
            : row[index];
        });

        const formattedRow = templateUploadConfig?.formatter
          ? templateUploadConfig.formatter(rowData)
          : rowData;

        return formattedRow;
      });

      if (typeof uploadDataToBackend !== 'function') {
        console.error(
          "'uploadDataToBackend' function is missing. Check implementation.",
        );
        return;
      }

      uploadDataToBackend(dataToSend.filter((data: any) => data));
    };

    reader.onerror = () => {
      message.error('Failed to read the file');
    };

    reader.readAsArrayBuffer(file);
  };

  const uploadDataToBackend = async <T,>(dataToSend: T[]) => {
    if (dataToSend.length === 0) {
      message.error(`No data to upload. Fill in the template and try again.`);
      return;
    }

    const { duplicateKey, itemLabel } = templateUploadConfig || {};

    const filteredData = [...dataToSend].filter(
      (item, index, self) =>
        index ===
        self.findIndex(
          (t) =>
            t[duplicateKey as keyof typeof item] ===
            item[duplicateKey as keyof typeof item],
        ),
    );

    try {
      await Promise.all(
        filteredData.map(async (item: T) => {
          try {
            await createGQL({
              variables: {
                data: { ...item },
              },
            });
          } catch (error) {
            message.error(
              `Failed to upload data. Check record with ${itemLabel}: ${
                item[duplicateKey as keyof typeof item]
              }`,
            );
            console.error(error);
            return;
          }
        }),
      );
      message.success(
        `Successfully uploaded ${filteredData.length} ${itemLabel}(s).`,
      );
    } catch (error) {
      message.error(`An error occurred while uploading ${itemLabel}(s).`);
    }
  };

  const customRequest = async (options: any) => {
    const { file } = options;

    try {
      await handleFileUpload(file, templateColumns!);

      console.log('subido!!');
      options.onSuccess(null, file);
    } catch (error) {
      options.onError(error);
    }
  };

  useEffect(() => {
    if (isLoading) setIsModalOpen(false);
  }, [isLoading]);

  useEffect(() => {
    if (inlineEditing && isDeleteInlineModalOpen)
      setIsDeleteInlineModalOpen(false);
  }, [isLoading]);

  useEffect(() => {
    if (forceSubmit && forceSubmitRecordIndex) {
      const currentRecord = filterRecordById(data, forceSubmitRecordIndex);
      onSubmit(currentRecord);
    }
  }, [forceSubmit, forceSubmitRecordIndex]);

  const items: MenuItem[] = [
    {
      key: 'sub1',
      icon: <SettingOutlined style={{ color: '#437EF7' }} />,
      children: [
        onRefresh
          ? {
              key: '1-1',
              label: 'Reload',
              type: 'group',
              children: [
                {
                  key: '1',
                  label: (
                    <div className="flex items-center gap-2">
                      <ReloadOutlined
                        style={{ color: '#437EF7', margin: 0 }}
                        className="hover:cursor-pointer"
                      />
                      Refresh Data
                    </div>
                  ),
                  onClick: onRefresh,
                },
              ],
            }
          : null,
        templateColumns
          ? {
              key: '1-2',
              label: 'Upload',
              type: 'group',
              children: [
                {
                  key: '1',
                  label: (
                    <div className="w-full">
                      <Upload
                        customRequest={customRequest}
                        accept=".xlsx"
                        showUploadList={false}
                        className="w-full"
                      >
                        <div className="flex items-center w-full">
                          <UploadOutlined
                            style={{ color: '#437EF7', margin: 0 }}
                            className="hover:cursor-pointer"
                          />
                          <span className="flex-1">Bulk Data</span>
                        </div>
                      </Upload>
                    </div>
                  ),
                },
              ],
            }
          : null,
        !templateColumns && !exportExcelColumns
          ? null
          : {
              key: '1-3',
              label: 'Download',
              type: 'group',
              children: [
                canExport && templateColumns
                  ? {
                      key: '3',
                      label: (
                        <div className="flex items-center gap-2">
                          <FileExcelTwoTone
                            style={{ color: '#437EF7', margin: 0 }}
                            className="hover:cursor-pointer"
                          />
                          Template
                        </div>
                      ),
                      onClick: () =>
                        generateExcelTemplate('template', templateColumns),
                    }
                  : undefined,
                canExport && canViewFinancials && exportExcelColumns
                  ? {
                      key: '4',
                      label: (
                        <div className="flex items-center gap-2">
                          <DownloadOutlined
                            style={{ color: '#437EF7' }}
                            className="hover:cursor-pointer"
                          />
                          Export to Excel
                        </div>
                      ),
                      onClick: () =>
                        exportToExcel(
                          data,
                          exportExcelColumns,
                          'sample-table.xlsx',
                          childrenColumnName,
                        ),
                    }
                  : undefined,
              ].filter(Boolean) as MenuItem[],
            },
      ],
    },
  ];

  const shouldShouldMenuOptions =
    ((items[0] as ItemType)?.children ?? []).filter(Boolean).length > 0;

  const headerContent = (
    <section
      className={`flex items-center bg-silver pl-4 pr-6 justify-between`}
    >
      {title && typeof title === 'string' && (
        <span className="w-full text-sm ml-3 lg:ml-0 lg:text-base font-bold text-primary-200 min-w-max">
          {title}
        </span>
      )}
      <FilterHeader
        searchTerm={searchTerm}
        onSearch={handleSearch}
        className={`${
          title && typeof title === 'string'
            ? '!justify-end'
            : '!justify-between'
        }`}
        placeholder={searchPlaceholder}
      />
      <div className="py-2" onClick={(e) => e.stopPropagation()}>
        {shouldShouldMenuOptions && (
          <Menu
            mode="vertical"
            items={shouldShouldMenuOptions ? items : []}
            className="custom-menu"
            style={{ background: 'transparent' }}
          />
        )}
        {extendOptions && !title && extendOptions}
      </div>
    </section>
  );

  const tableContent = (
    <div>
      {selectedRows && selectedRows.length > 1 && (
        <Space className="w-full flex justify-end px-6 py-2">
          {MultiSelectComponent}
        </Space>
      )}
      <Table
        bordered={isMatrix}
        size={tableSize ?? 'middle'}
        components={{
          body: {
            ...(inlineEditing
              ? { cell: isMatrix ? EditableCellMitigation : EditableCell }
              : { row: MemoizedRow }),
          },
        }}
        rowKey={(record) =>
          (rowKey && record[rowKey]) ||
          record?.uuid ||
          (record?.key as string) ||
          (record?.id as string)
        }
        columns={
          !inlineEditing
            ? customColumns
            : isMatrix
            ? inlineEditingColumnsMitigation
            : (inlineEditingColumns as any)
        }
        dataSource={filteredData}
        pagination={{
          pageSize: pageSize || 10,
          hideOnSinglePage: true,
          showSizeChanger: false,
        }}
        className={`ant-table-wrapper--no-borders ant-table-wrapper--no-radius custom-table--heading nested-columns--custom expandable-icon--custom pb-10 ${customStyles}`}
        loading={loading || isLoading}
        summary={summary}
        scroll={scroll}
        expandable={{
          defaultExpandAllRows: false,
          childrenColumnName: childrenColumnName || 'children',
        }}
        rowClassName={(record) =>
          record?.[childrenColumnName || 'children'] &&
          record?.[childrenColumnName || 'children']?.length > 0
            ? 'expandable-row'
            : 'non-expandable-row'
        }
        rowSelection={rowSelection}
      />
    </div>
  );

  const content = (
    <Form form={form} layout="vertical" component={false}>
      {collapsible ? (
        <Collapse
          defaultActiveKey={defaultCollapsed ? [] : ['table']}
          onChange={handleCollapseChange}
          bordered={false}
          className="table-collapse"
          expandIcon={({ isActive }) => (
            <div className="flex items-center gap-4 w-24">
              <span className="underline text-base text-gray-600 w-16 text-center">
                {isActive ? 'Collapse' : 'Expand'}
              </span>
              <span>{isActive ? <ChevronUpIcon /> : <ChevronDownIcon />}</span>
            </div>
          )}
        >
          <Panel
            header={headerContent}
            key="table"
            showArrow={true}
            className="table-panel"
          >
            {tableContent}
          </Panel>
        </Collapse>
      ) : (
        <>
          {headerContent}
          {tableContent}
        </>
      )}
      {canMakeCreateActions && (
        <Button
          type="primary"
          onClick={handleAddRecord}
          className="min-w-[188px] mx-auto mb-5 text-sm"
        >
          + Add
        </Button>
      )}
    </Form>
  );

  return (
    <>
      {contextHolder}
      {useBorderedSection ? (
        <BorderedSection
          title={hideTableHeading ? undefined : renderTitle}
          noPadding
        >
          {content}
        </BorderedSection>
      ) : (
        content
      )}
      <AntModal
        title={
          typeof title === 'function'
            ? title(currentRecord)
            : `${currentRecord ? 'Edit' : 'Add'} ${title}`
        }
        openModal={isModalOpen}
        onCancel={handleCloseModal}
      >
        <AutomatedForm
          fields={formFields}
          editFields={editFormFields}
          currentRecord={currentRecord}
          canDelete={editableOptions.delete}
          onSubmit={isMatrix ? onMatrixSubmit : onSubmit}
          onDelete={onDelete}
          formInstance={formInstance}
        />
      </AntModal>
      <DeleteModal
        title={'DELETE'}
        isOpen={isDeleteInlineModalOpen}
        onCancel={() => setIsDeleteInlineModalOpen(false)}
        onDelete={onDelete}
      />
    </>
  );
};

export default TableV3;
